import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Building2, Users, KanbanSquare, TrendingUp, ArrowRight, Database, Sparkles, Briefcase, BookOpen, FileText, Download, Video, Code2, PlayCircle, ShieldCheck, Wand2,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useDemoMode } from "@/contexts/DemoModeContext";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid, Cell,
} from "recharts";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import AICoachWidget from "@/components/AICoachWidget";
import HotProspectsWidget from "@/components/HotProspectsWidget";
import HotLeadsWidget from "@/components/HotLeadsWidget";
import CoverageHeatMap from "@/components/CoverageHeatMap";
import MorningRoutineWidget from "@/components/MorningRoutineWidget";
import IntegrationsHealth from "@/components/IntegrationsHealth";
import ApiQuotaBanner from "@/components/ApiQuotaBanner";
import { useAuth } from "@/contexts/AuthContext";

const COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#a78bfa", "#f43f5e", "#64748b"];

function useCountUp(target, duration = 700) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (typeof target !== "number" || Number.isNaN(target)) { setV(target); return; }
    const start = performance.now();
    const from = 0;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setV(Math.round(from + (target - from) * ease(p)));
      if (p < 1) ref.current = requestAnimationFrame(tick);
    };
    ref.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.current);
  }, [target, duration]);
  return v;
}

function Kpi({ label, value, hint, icon: Icon, testid }) {
  const isNumeric = typeof value === "number";
  const display = useCountUp(isNumeric ? value : 0);
  return (
    <Card className="border-border" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="mt-1 text-2xl font-medium tabular-nums tracking-tight">{isNumeric ? display : value}</div>
        {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const isAdmin = user?.role === "ADMIN";
  const { start: startDemo } = useDemoMode();

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/stats");
      setData(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const [quality, setQuality] = useState(null);
  const loadQuality = async () => {
    try {
      const { data } = await api.get("/quality-audit/summary");
      setQuality(data?.score || null);
    } catch (_e) { /* silent */ }
  };

  useEffect(() => { load(); loadQuality(); }, []);

  const seed = async () => {
    try {
      const { data: res } = await api.post("/seed/demo");
      const total = (res.companies || 0) + (res.deals || 0) + (res.countries || 0) + (res.sectors || 0);
      if (total === 0) {
        toast.info(t("dashboard.demo_data_already_present"));
      } else {
        toast.success(t("dashboard.demo_data_added", {
          companies: res.companies || 0,
          deals: res.deals || 0,
        }));
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Download via window.open() to break out of iframe sandbox (Chrome desktop fix)
  const triggerDownload = (path, filename, toastKey) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${base}/api${path}`;
    // Open in a new tab. With Content-Disposition: attachment, browser auto-closes the tab and triggers the download.
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      // Pop-up blocked — fallback to anchor with target=_blank
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => a.remove(), 1000);
    }
    if (toastKey) setTimeout(() => toast.success(t(toastKey)), 400);
  };

  const downloadPitch = () => {
    const lang = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
    const fname = lang === "fr" ? "Nexaforge-Presentation-FR.pdf" : "Nexaforge-Pitch-Deck-EN.pdf";
    triggerDownload(`/pdf/pitch?lang=${lang}`, fname, "dashboard.pitch_downloaded");
  };

  const downloadTestGuideNative = () =>
    triggerDownload("/pdf/test-guide", "nexaforge-guide-test-manuel.pdf", "dashboard.test_guide_downloaded");

  const downloadVideoKit = () =>
    triggerDownload("/pdf/video-kit", "Nexaforge-Kit-Video.pdf", "dashboard.video_kit_downloaded");

  const downloadAutoDemoScript = () =>
    triggerDownload("/pdf/auto-demo-script", "auto_demo_recording.py", "dashboard.auto_demo_downloaded");

  if (!data) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-muted/50 animate-pulse rounded-lg" />)}
        </div>
      </div>
    );
  }

  const { kpis, by_country, by_sector, top_deals, top_score_companies, pipeline_evolution, recent_activities } = data;
  const isEmpty = kpis.companies === 0;

  return (
    <div>
      <ApiQuotaBanner />
      <PageHeader
        title={t("dashboard.title")}
        description={t("dashboard.subtitle")}
        actions={
          <>
            {isAdmin && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="demo-kit-btn">
                      <Download className="h-3.5 w-3.5 mr-1.5" /> {t("dashboard.demo_kit")}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      {t("dashboard.demo_kit_label")}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => {
                        toast.info(t("demo.starting_hint"));
                        startDemo();
                      }}
                      data-testid="dk-guided-demo"
                      className="cursor-pointer focus:bg-emerald-50 dark:focus:bg-emerald-950/40"
                    >
                      <PlayCircle className="h-4 w-4 mr-2 text-emerald-600" />
                      <div className="flex-1">
                        <div className="text-sm font-semibold">{t("dashboard.guided_demo")}</div>
                        <div className="text-[11px] text-muted-foreground">{t("dashboard.guided_demo_hint")}</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={downloadPitch} data-testid="dk-pitch">
                      <FileText className="h-4 w-4 mr-2 text-emerald-600" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{t("dashboard.pitch_pdf")}</div>
                        <div className="text-[11px] text-muted-foreground">{t("dashboard.pitch_hint")}</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadVideoKit} data-testid="dk-video">
                      <Video className="h-4 w-4 mr-2 text-emerald-600" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{t("dashboard.video_kit")}</div>
                        <div className="text-[11px] text-muted-foreground">{t("dashboard.video_kit_hint")}</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadAutoDemoScript} data-testid="dk-auto">
                      <Code2 className="h-4 w-4 mr-2 text-emerald-600" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{t("dashboard.auto_demo_script")}</div>
                        <div className="text-[11px] text-muted-foreground">{t("dashboard.auto_demo_hint")}</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={downloadTestGuideNative} data-testid="dk-test">
                      <BookOpen className="h-4 w-4 mr-2 text-slate-500" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{t("dashboard.test_guide")}</div>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="outline" size="sm" onClick={seed} data-testid="seed-demo-btn">
                  <Database className="h-3.5 w-3.5 mr-1.5" /> {t("dashboard.demo_data")}
                </Button>
              </>
            )}
            <Button size="sm" onClick={() => navigate("/companies")} data-testid="dashboard-add-company">
              {t("dashboard.new_company")} <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </>
        }
      />

      {isEmpty && (
        <Card className="mb-6 ai-accent" data-testid="empty-state-card">
          <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-medium tracking-tight">{t("dashboard.empty_title")}</div>
              <div className="text-sm text-muted-foreground">{t("dashboard.empty_subtitle")}</div>
            </div>
            <Button onClick={seed} data-testid="empty-state-seed">
              <Database className="h-3.5 w-3.5 mr-1.5" /> {t("dashboard.load_demo")}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Phase 5D — AI Coach prescriptive recommendations + Hot Prospects */}
      <div className="grid lg:grid-cols-3 gap-3 mb-6">
        <div className="lg:col-span-2">
          <AICoachWidget />
        </div>
        <HotProspectsWidget />
      </div>

      {/* Hot leads à relancer (top warm/hot contacts) */}
      <div className="mb-6">
        <HotLeadsWidget />
      </div>

      {/* Integrations Health — compact overview, full details in Settings */}
      <div className="mb-6">
        <IntegrationsHealth variant="compact" />
      </div>

      {/* Coverage Heat Map — Mandant × Country */}
      <div className="mb-6">
        <CoverageHeatMap />
      </div>

      {/* Morning Routine — on-demand suggestions to fill gaps */}
      <div className="mb-6">
        <MorningRoutineWidget />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 fade-up-stagger">
        <Kpi label={t("dashboard.kpi_companies")} value={kpis.companies} icon={Building2} testid="kpi-companies" />
        <Kpi label={t("dashboard.kpi_contacts")} value={kpis.contacts} icon={Users} testid="kpi-contacts" />
        <Kpi label={t("dashboard.kpi_active_deals")} value={kpis.active_deals} icon={KanbanSquare} testid="kpi-deals"
             hint={t("dashboard.kpi_pipeline_value", { value: kpis.pipeline_value.toLocaleString() })} />
        <Kpi label={t("dashboard.kpi_conversion")} value={`${kpis.conversion_rate}%`} icon={TrendingUp} testid="kpi-conversion" />
      </div>

      {quality && (
        <Card
          className="border-border mb-6 cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => navigate("/quality-audit")}
          data-testid="data-health-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center gap-5">
              {/* Gauge */}
              <div className="relative h-20 w-20 shrink-0">
                <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
                  <circle cx="18" cy="18" r="15.915" fill="none" stroke="hsl(var(--muted))" strokeWidth="2.5" />
                  <circle
                    cx="18" cy="18" r="15.915" fill="none"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeDasharray={`${quality.value}, 100`}
                    className={
                      quality.tier === "excellent" ? "stroke-emerald-500"
                      : quality.tier === "good" ? "stroke-sky-500"
                      : quality.tier === "fair" ? "stroke-amber-500"
                      : "stroke-rose-500"
                    }
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xl font-medium tabular-nums leading-none">{quality.value}</span>
                  <span className="text-[9px] text-muted-foreground mt-0.5">/100</span>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                  <span className="text-sm font-medium">{t("dashboard.data_health_title")}</span>
                  <Badge
                    variant="outline"
                    className={
                      "text-[10px] uppercase tracking-wider " +
                      (quality.tier === "excellent" ? "border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40"
                      : quality.tier === "good" ? "border-sky-300 text-sky-700 bg-sky-50 dark:bg-sky-950/40"
                      : quality.tier === "fair" ? "border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-950/40"
                      : "border-rose-300 text-rose-700 bg-rose-50 dark:bg-rose-950/40")
                    }
                  >
                    {t(`dashboard.data_health_tier_${quality.tier}`)}
                  </Badge>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {t("dashboard.data_health_breakdown", {
                    verified: quality.verified_ratio,
                    dm: quality.dm_ratio,
                  })}
                </div>
              </div>

              <Button
                size="sm"
                variant="outline"
                className="shrink-0 h-7 text-[11px] border-violet-400 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-950/40"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!window.confirm("Lancer la maintenance auto complète maintenant ?\n\n→ Apollo cross-check apply\n→ Hunter décideurs\n→ Email verify\n→ Auto-merge doublons\n\nDurée ~3-8 min, résultat dans une notification.")) return;
                  api.post("/quality-audit/auto-mode/run-now")
                    .then(() => toast.info("Maintenance lancée — résultat dans quelques minutes", { duration: 8000 }))
                    .catch((err) => toast.error(apiError(err)));
                }}
                data-testid="dh-quick-run-btn"
              >
                <Wand2 className="h-3 w-3 mr-1" />
                Tout automatiser
              </Button>
              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-3 mb-6">
        <Card className="lg:col-span-2 border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t("dashboard.pipeline_evolution")}</CardTitle>
          </CardHeader>
          <CardContent className="pl-2">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={pipeline_evolution}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                  labelStyle={{ color: "hsl(var(--foreground))" }}
                />
                <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} name="Valeur pipeline (€)" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t("dashboard.by_sector")}</CardTitle>
          </CardHeader>
          <CardContent>
            {by_sector.length === 0 ? (
              <div className="text-xs text-muted-foreground py-8 text-center">{t("dashboard.no_data")}</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(220, Math.min(by_sector.length, 10) * 28)}>
                <BarChart
                  data={by_sector
                    .slice(0, 10)
                    .map((s, i) => ({
                      name: s.sector.name,
                      shortName: s.sector.name.length > 18 ? s.sector.name.slice(0, 17) + "…" : s.sector.name,
                      count: s.count,
                      fill: COLORS[i % COLORS.length],
                    }))}
                  layout="vertical"
                  margin={{ top: 4, right: 12, bottom: 4, left: 0 }}
                >
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="shortName"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    width={150}
                    interval={0}
                  />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ""}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {by_sector.slice(0, 10).map((s, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mb-6">
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> {t("dashboard.top_companies_ai")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_score_companies.length === 0 ? (
              <div className="text-xs text-muted-foreground py-6 text-center">
                {t("dashboard.no_scored")}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {top_score_companies.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between py-2.5 cursor-pointer hover:bg-muted/40 px-2 -mx-2 rounded"
                    onClick={() => navigate(`/companies/${c.id}`)}
                    data-testid={`top-score-row-${c.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center text-xs font-medium">
                        {c.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{c.name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {c.country?.flag_emoji} {c.country?.name} · {c.sector?.name}
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline" className="font-mono text-xs tabular-nums">
                      {c.ai_score}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t("dashboard.top_deals")}</CardTitle>
          </CardHeader>
          <CardContent>
            {top_deals.length === 0 ? (
              <div className="text-xs text-muted-foreground py-6 text-center">{t("dashboard.no_deals")}</div>
            ) : (
              <div className="divide-y divide-border">
                {top_deals.map((d) => (
                  <div
                    key={d.id} className="py-2.5 cursor-pointer hover:bg-muted/40 px-2 -mx-2 rounded"
                    onClick={() => navigate("/pipeline")}
                  >
                    <div className="flex justify-between items-center">
                      <div className="text-sm font-medium truncate">{d.title}</div>
                      <div className="text-sm font-mono tabular-nums">
                        {(d.value || 0).toLocaleString("fr-FR")} {d.currency || "EUR"}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <div className="text-xs text-muted-foreground">{d.company?.name}</div>
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{d.stage}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Briefcase className="h-3.5 w-3.5" /> {t("dashboard.countries_title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {by_country.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-6">{t("dashboard.no_data")}</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {by_country.map((c) => (
                <div key={c.country.code} className="border border-border rounded-md px-3 py-2 text-sm flex items-center gap-2 hover:bg-muted/40 cursor-pointer"
                     onClick={() => navigate("/companies")}>
                  <span className="text-base">{c.country.flag_emoji}</span>
                  <span>{c.country.name}</span>
                  <Badge variant="secondary" className="ml-1 font-mono tabular-nums">{c.count}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
